'use client';

import { type ChangeEvent, type ChangeEventHandler, type HTMLProps, type ReactNode, type Ref, useCallback, useEffect, useId, useState } from 'react';
import { clsx } from 'clsx';
import { useWebMask } from 'react-hook-mask';
import ClearIcon from '../../../icons/react/icon-clear';
import InfoIcon from '../../../icons/react/icon-info';
import { HorizontalStack } from '../../alignment/horizontal-stack';
import { VerticalStack } from '../../alignment/vertical-stack';
import { PlainIconButton } from '../../button';
import { FormHint, type FormHintVariant } from '../../form/hint';
import { Tooltip } from '../../tooltip/tooltip';
import { ErrorMessage } from '../error-message/error-message';
import { Label } from '../label/label';
import { useFocusWithin } from '../shared/use-focus-within';
import type { MaskOptions } from './masks';
import inputStyles from '../input.module.scss';
import styles from './text-input.module.scss';

type InputVariant = 'default' | 'monospace';

type TextInputSize = 'small' | 'medium';

export interface TextInputProps extends Omit<HTMLProps<HTMLInputElement>, 'prefix' | 'size'> {
  allowClear?: boolean;
  appearance?: 'default' | 'error';
  containerClassName?: string;
  disabled?: boolean;
  error?: ReactNode;
  hideLabel?: boolean;
  hint?: ReactNode;
  hintVariant?: FormHintVariant;
  id?: string;
  info?: string;
  infoWidth?: number;
  inputClassName?: string;
  keepMask?: boolean;
  label: string;
  maskOptions?: MaskOptions;
  name: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  prefix?: ReactNode;
  ref?: Ref<HTMLInputElement>;
  required?: boolean;
  size?: TextInputSize;
  suffix?: ReactNode;
  suffixClassName?: string;
  touched?: boolean;
  value: string | number;
  variant?: InputVariant;
}

export function TextInput({
  allowClear = false,
  appearance = 'default',
  className,
  containerClassName,
  disabled,
  error,
  hideLabel,
  hint,
  hintVariant,
  id,
  info,
  infoWidth,
  inputClassName,
  keepMask,
  label,
  maskOptions,
  name,
  onChange,
  placeholder: providedPlaceholder,
  prefix,
  ref,
  required,
  size = 'medium',
  suffix,
  suffixClassName,
  touched,
  value,
  variant = 'default',
  ...rest
}: TextInputProps) {
  const [hasHadValue, setHasHadValue] = useState(Boolean(value));
  const generatedId = useId();
  const usableId = id ?? generatedId;
  const handleClear = useCallback(() => {
    onChange?.({ target: { value: '', name } } as ChangeEvent<HTMLInputElement>);
  }, [onChange, name]);
  const maskedChangeHandler = useCallback(
    (newValue: string) => {
      if (maskOptions) {
        onChange?.({ target: { value: newValue, name } } as ChangeEvent<HTMLInputElement>);
      }
    },
    [maskOptions, onChange, name],
  );

  const {
    value: maskedValue,
    onChange: maskedOnChange,
    ref: maskRef,
  } = useWebMask({
    maskGenerator: maskOptions,
    value: value?.toString() ?? '',
    onChange: maskedChangeHandler,
    keepMask,
    ref: maskOptions ? ref : null,
  });
  const [hasFocus, handleFocusWithin, handleBlurWithin] = useFocusWithin();
  const placeholder = providedPlaceholder ?? maskOptions?.emptyValue;
  const showErrorState = Boolean(touched && error && hasHadValue);
  const showError = Boolean(showErrorState && !hasFocus);
  const isRenderingEmptyMask = maskOptions?.emptyValue && !maskedValue;
  const errorId = `${usableId}-error`;
  const hintId = `${usableId}-hint`;
  const showValue = Boolean(!hideLabel && (value || placeholder || hasFocus));
  const inputRef = maskOptions ? maskRef : ref;
  const hasValue = Boolean(value);
  const forceErrorAppearance = appearance === 'error';

  useEffect(() => {
    if (hasValue) {
      setHasHadValue(true);
    }
  }, [hasValue]);

  return (
    <VerticalStack
      className={clsx(
        inputStyles.input,
        styles.textInput,
        showValue && styles.hasValue,
        disabled && inputStyles.isDisabled,
        styles[`size-${size}`],
        containerClassName,
      )}
      onBlurCapture={handleBlurWithin}
      onFocusCapture={handleFocusWithin}
    >
      <HorizontalStack
        align="space-between"
        blockAlign="center"
        className={clsx(inputStyles.field, styles.fieldContainer, styles.inputElement, className)}
        wrap="nowrap"
      >
        <Label
          hasError={forceErrorAppearance || showErrorState}
          htmlFor={usableId}
          isFloating={showValue}
          isHidden={hideLabel}
          label={label}
          required={required}
        />

        {prefix ? <div className={clsx(styles.affix, styles.prefix)}>{prefix}</div> : null}

        <input
          aria-describedby={hint ? hintId : undefined}
          aria-errormessage={forceErrorAppearance || showError ? errorId : undefined}
          aria-invalid={Boolean(error)}
          aria-required={required}
          className={clsx(styles.field, styles[`variant-${variant}`], isRenderingEmptyMask && styles.isEmptyMask, inputClassName)}
          disabled={disabled}
          id={usableId}
          name={name}
          onChange={maskOptions ? maskedOnChange : onChange}
          placeholder={placeholder}
          {...rest}
          ref={inputRef}
          required={required}
          value={maskOptions ? maskedValue : value}
        />

        {(value && allowClear) || suffix || info ? (
          <div className={clsx(styles.affix, styles.suffix, suffixClassName)}>
            <HorizontalStack blockAlign="center" gap={size === 'small' ? 2 : 4}>
              {suffix}
              {value && allowClear ? (
                <button aria-label="Clear value" className={styles.clearButton} disabled={disabled} onClick={handleClear} type="button">
                  <ClearIcon aria-hidden height={16} />
                </button>
              ) : null}
              {info ? (
                <Tooltip
                  activator={
                    <PlainIconButton>
                      <InfoIcon aria-hidden height={16} />
                    </PlainIconButton>
                  }
                  maxWidth={infoWidth ?? 136}
                  side="top"
                  size="small"
                >
                  {info}
                </Tooltip>
              ) : null}
            </HorizontalStack>
          </div>
        ) : null}
      </HorizontalStack>

      {hint ? (
        <FormHint className={styles.inputElement} id={hintId} variant={hintVariant}>
          {hint}
        </FormHint>
      ) : null}
      {showError || (forceErrorAppearance && error) ? (
        <ErrorMessage className={styles.inputElement} id={errorId}>
          {error}
        </ErrorMessage>
      ) : null}
    </VerticalStack>
  );
}
